// src/components/ui/Button/Button.jsx
import React from 'react';
import styles from './Button.module.css';

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  disabled = false,
  onClick,
  type = 'button',
  className = '',
  ...props
}) => {
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: 'none',
    borderRadius: '100px',
    cursor: isLoading || disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    textDecoration: 'none',
    fontWeight: 500,
    fontFamily: 'inherit',
    opacity: isLoading || disabled ? 0.6 : 1,
  };

  const variants = {
    primary: {
      backgroundColor: 'rgb(var(--c-accent))',
      color: 'rgb(var(--c-on-accent))',
      ':hover': { backgroundColor: '#1A4A7A' },
    },
    secondary: {
      backgroundColor: 'transparent',
      color: 'rgb(var(--c-text))',
      border: '1px solid rgb(var(--c-border))',
      ':hover': { borderColor: 'rgb(var(--c-text))' },
    },
    outline: {
      backgroundColor: 'transparent',
      color: 'rgb(var(--c-accent))',
      border: '1px solid rgb(var(--c-accent))',
      ':hover': { backgroundColor: 'rgb(var(--c-accent))', color: 'rgb(var(--c-on-accent))' },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: 'rgb(var(--c-text))',
      ':hover': { backgroundColor: 'rgb(var(--c-surface-2))' },
    },
    danger: {
      backgroundColor: 'rgb(var(--c-danger))',
      color: 'rgb(var(--c-on-accent))',
      ':hover': { backgroundColor: '#B02A37' },
    },
    success: {
      backgroundColor: 'rgb(var(--c-success))',
      color: 'rgb(var(--c-on-accent))',
      ':hover': { backgroundColor: '#1E7E34' },
    },
  };

  const sizes = {
    sm: { padding: '6px 16px', fontSize: '12px' },
    md: { padding: '10px 24px', fontSize: '14px' },
    lg: { padding: '14px 32px', fontSize: '16px' },
  };

  // ============================================================
  // 🔴 БЫЛ БАГ: КНОПКА ТЕРЯЛА ВСЕ СТИЛИ, ЕСЛИ ЕЙ ПЕРЕДАВАЛИ `style`.
  //
  //    Было: `<button style={style} ... {...props} />`. Свойства из `props`
  //    ставились ПОСЛЕ `style`, поэтому переданный снаружи
  //    `<Button style={{ marginTop: '16px' }}>` перекрывал ВЕСЬ набор
  //    стилей кнопки — и от кнопки оставалась прозрачная рамка с текстом.
  //    Замер: `background: rgba(0,0,0,0)`, `color: blue`, высота 24 px
  //    вместо фона акцента (поймано на снимке корзины: «Оформить заказ»
  //    выглядел синей ссылкой).
  //
  //    📌 Почему долго не замечали: кнопки БЕЗ `style` работали. Дефект
  //       был в 12 точках проекта (избранное, каталог, кейсы, админка,
  //       корзина, оформление) — то есть выглядел как «разные кнопки
  //       сделаны по-разному», а не как один общий баг.
  //       Тот же класс, что §7 №1: неверное поведение без ошибки в консоли.
  //
  //    ✅ Лечение: стили кнопки — база, а `props.style` ПОВЕРХ неё.
  //       Внешний стиль ДОПОЛНЯЕТ кнопку (отступ), а не заменяет.
  //       Остальные `props` по-прежнему поверх — так и задумано
  //       (`onClick`, `type`, `disabled` объявлены явно и не прокидываются).
  // ============================================================
  const { style: externalStyle, ...rest } = props;

  const style = {
    ...baseStyles,
    ...variants[variant],
    ...sizes[size],
    ...(fullWidth && { width: '100%' }),
    ...externalStyle,
  };

  return (
    <button
      type={type}
      style={style}
      onClick={onClick}
      disabled={isLoading || disabled}
      className={className}
      {...rest}
    >
      {isLoading ? '⏳' : children}
    </button>
  );
};